import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { HERO_IMAGE, MENU_FEATURED_IMAGE, MEMBERSHIP_IMAGE, PUMABILT_IMAGE, BOTANICAL_IMAGE, MENU } from "@/data/content";
import { fetchHouseListCount } from "@/lib/api";

const FOUNDING_TOTAL = 111;

function useFoundingCount() {
  const [count, setCount] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchHouseListCount()
      .then((r) => { if (alive) setCount(Math.min(Number(r?.count) || 0, FOUNDING_TOTAL)); })
      .catch(() => { if (alive) setCount(0); });
    return () => { alive = false; };
  }, []);
  return count;
}

export default function Home() {
  const claimed = useFoundingCount();
  const claimedDisplay = claimed === null ? "—" : String(claimed).padStart(3, "0");
  const remaining = claimed === null ? null : Math.max(FOUNDING_TOTAL - claimed, 0);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section data-testid="home-hero" className="relative h-[100svh] min-h-[640px] w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="The Uncanny House"
            className="h-full w-full object-cover animate-slow-zoom"
          />
          <div className="absolute inset-0 bg-obsidian/55" />
          <div className="absolute inset-x-0 top-0 h-40 scrim-top" />
          <div className="absolute inset-x-0 bottom-0 h-60 scrim-bottom" />
        </div>

        <div className="relative z-10 h-full mx-auto max-w-[1400px] px-6 md:px-10 flex items-end pb-16 md:pb-24">
          <div className="max-w-3xl">
            <p data-testid="hero-eyebrow" className="label-eyebrow text-brass animate-fade-up" style={{ animationDelay: "0.1s" }}>
              Est. — A Private Coffee House on Wheels
            </p>
            <h1
              data-testid="hero-title"
              className="font-display text-bone text-[44px] sm:text-6xl md:text-7xl lg:text-[96px] leading-[0.95] mt-6 animate-fade-up"
              style={{ animationDelay: "0.25s" }}
            >
              The <em className="not-italic text-brass">Uncanny</em>
              <br />
              House.
            </h1>
            <p
              data-testid="hero-tagline"
              className="mt-6 font-display italic text-bone/85 text-2xl md:text-3xl animate-fade-up"
              style={{ animationDelay: "0.45s" }}
            >
              Crafted for Obsession.
            </p>
            <p
              data-testid="hero-description"
              className="mt-5 max-w-xl text-bone/70 text-base md:text-lg leading-relaxed animate-fade-up"
              style={{ animationDelay: "0.6s" }}
            >
              A place for the curious, the indulgent, and those always looking for what&apos;s next.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4 animate-fade-up" style={{ animationDelay: "0.8s" }}>
              <Link
                to="/menu"
                data-testid="hero-cta-menu"
                className="group inline-flex items-center justify-center gap-2 px-7 py-4 bg-brass text-obsidian text-[12px] uppercase tracking-[0.28em] hover:bg-champagne transition-colors duration-300"
              >
                View the Menu
                <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/membership"
                data-testid="hero-cta-house-list"
                className="group inline-flex items-center justify-center gap-2 px-7 py-4 border border-bone/30 text-bone text-[12px] uppercase tracking-[0.28em] hover:border-lavender hover:text-lavender transition-colors duration-300"
              >
                Join The House List
                <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>
        </div>

        {/* corner marks — live founding members counter */}
        <Link
          to="/membership"
          data-testid="founding-counter"
          className="absolute top-24 right-6 md:right-10 z-10 text-right hidden sm:block group"
          aria-label={`Founding obsessions claimed: ${claimedDisplay} of ${FOUNDING_TOTAL}`}
        >
          <p className="label-eyebrow text-bone/60 group-hover:text-brass transition-colors">
            N° <span data-testid="founding-counter-claimed" className="text-brass tabular-nums">{claimedDisplay}</span>
            <span className="text-bone/40"> / {FOUNDING_TOTAL}</span>
          </p>
          <p className="font-display italic text-bone/80 mt-1 group-hover:text-bone transition-colors">
            Founding Obsessions Claimed
          </p>
          {remaining !== null && remaining > 0 && (
            <p data-testid="founding-counter-remaining" className="label-eyebrow text-lavender mt-2 text-[10px]">
              {remaining} {remaining === 1 ? "Number" : "Numbers"} Remain
            </p>
          )}
          {remaining === 0 && (
            <p data-testid="founding-counter-closed" className="label-eyebrow text-lavender mt-2 text-[10px]">
              The Door Is Closed
            </p>
          )}
        </Link>
      </section>

      {/* MARQUEE */}
      <section data-testid="home-marquee" className="border-y border-bone/10 bg-obsidian overflow-hidden">
        <div className="flex animate-marquee gap-12 py-5 whitespace-nowrap">
          {["a", "b"].map((row) => (
            <div key={`marquee-row-${row}`} className="flex items-center gap-12 text-[11px] uppercase tracking-[0.4em] text-bone/55 shrink-0" aria-hidden={row === "b" ? "true" : undefined}>
              <span>Cold Brew, slow steeped 18 hrs</span><span className="text-brass">◆</span>
              <span>Ceremonial Matcha, stone milled</span><span className="text-brass">◆</span>
              <span>Limited dessert drops</span><span className="text-brass">◆</span>
              <span>Stewards-first drops every Friday</span><span className="text-brass">◆</span>
              <span>Booked into 38 private events</span><span className="text-brass">◆</span>
            </div>
          ))}
        </div>
      </section>

      {/* MANIFESTO */}
      <section data-testid="home-manifesto" className="relative py-24 md:py-32 px-6 md:px-10">
        <div className="mx-auto max-w-[1400px] grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-4">
            <p className="label-eyebrow">A Brief Statement</p>
            <div className="mt-6 hairline" />
          </div>
          <div className="md:col-span-8">
            <p className="font-display text-bone text-3xl md:text-5xl leading-[1.1]">
              We do not run a coffee shop. We run a house.
            </p>
            <p className="mt-6 font-display italic text-lavender text-3xl md:text-5xl leading-[1.1]">
              Enter The Uncanny House.
            </p>
            <p className="mt-8 font-display text-bone/85 text-xl md:text-2xl leading-[1.35] max-w-2xl">
              A place for the curious, the indulgent, and those always looking for what&apos;s next.
            </p>

            <div className="mt-10 space-y-2 font-display italic text-bone/75 text-lg md:text-xl leading-[1.5] max-w-2xl">
              <p>The satisfaction of a handcrafted drink.</p>
              <p>The weight of a brass handle.</p>
              <p>The crack of a pop top.</p>
              <p>The anticipation before the first sip.</p>
              <p>The moment a simple coffee becomes an experience.</p>
            </div>

            <p className="mt-10 font-display text-bone text-2xl md:text-3xl leading-[1.2] max-w-2xl">
              Every detail, thoughtfully curated.
            </p>
            <p className="mt-3 font-display text-bone text-2xl md:text-3xl leading-[1.2] max-w-2xl">
              Every visit, <em className="not-italic text-brass">Crafted for Obsession</em>.
            </p>

            <p className="mt-10 text-bone/65 max-w-2xl">
              Every cup is pulled to order. Every obsession begins in small batches. Every release is limited.
              Once it&apos;s gone, it&apos;s gone. There is no franchise. There never will be.
            </p>
          </div>
        </div>
      </section>

      {/* BENTO — featured pillars */}
      <section data-testid="home-pillars" className="relative px-6 md:px-10 pb-24 md:pb-32">
        <div className="mx-auto max-w-[1400px] grid grid-cols-12 gap-4 md:gap-6">
          {/* Menu */}
          <Link
            to="/menu"
            data-testid="pillar-menu"
            className="group relative col-span-12 md:col-span-7 row-span-2 aspect-[16/11] md:aspect-auto md:h-[560px] overflow-hidden border border-bone/10 hover:border-brass/40 transition-colors"
          >
            <img src={MENU_FEATURED_IMAGE} alt="" className="h-full w-full object-cover transition-transform duration-[1.6s] group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/95 via-obsidian/40 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8 md:p-10">
              <p className="label-eyebrow text-brass">01 — The Menu</p>
              <h3 className="font-display text-bone text-3xl md:text-5xl mt-4">Cold Brew, Lattes, The Dessert Parlor.</h3>
              <p className="mt-4 text-bone/70 max-w-md">Five chapters, rotating weekly. Espresso pulled to order. Cakes baked in the dark hours.</p>
            </div>
          </Link>

          {/* Pumabilt */}
          <Link
            to="/pumabilt"
            data-testid="pillar-pumabilt"
            className="group relative col-span-12 md:col-span-5 aspect-[4/3] md:aspect-auto md:h-[272px] overflow-hidden border border-bone/10 hover:border-brass/40 transition-colors"
          >
            <img src={PUMABILT_IMAGE} alt="" className="h-full w-full object-cover transition-transform duration-[1.6s] group-hover:scale-105" />
            <div className="absolute inset-0 bg-obsidian/55" />
            <div className="absolute inset-0 p-7 md:p-8 flex flex-col justify-between">
              <p className="label-eyebrow text-lavender">02 — The Mascot</p>
              <div>
                <h3 className="font-display text-bone text-3xl md:text-4xl">The Pumabilt.</h3>
                <p className="mt-2 text-bone/70 text-sm">A half-scale restored Peterbilt. Bone, brass &amp; lavender.</p>
              </div>
            </div>
          </Link>

          {/* Membership */}
          <Link
            to="/membership"
            data-testid="pillar-membership"
            className="group relative col-span-12 md:col-span-5 aspect-[4/3] md:aspect-auto md:h-[272px] overflow-hidden border border-bone/10 hover:border-brass/40 transition-colors"
          >
            <img src={MEMBERSHIP_IMAGE} alt="" className="h-full w-full object-cover transition-transform duration-[1.6s] group-hover:scale-105" />
            <div className="absolute inset-0 bg-obsidian/65" />
            <div className="absolute inset-0 p-7 md:p-8 flex flex-col justify-between">
              <p className="label-eyebrow text-brass">03 — The House</p>
              <div>
                <h3 className="font-display text-bone text-3xl md:text-4xl">Founding Steward Card.</h3>
                <p className="mt-2 text-bone/70 text-sm">Permanent Steward Numbers. 111 issued. Brass engraved.</p>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* FEATURED CHAPTERS */}
      <section data-testid="home-featured-menu" className="relative px-6 md:px-10 pb-24 md:pb-32">
        <div className="mx-auto max-w-[1400px]">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <p className="label-eyebrow">Selected From the Menu</p>
              <h2 className="font-display text-bone text-4xl md:text-6xl mt-4">Five chapters. Rotating weekly.</h2>
            </div>
            <Link to="/menu" data-testid="featured-menu-link" className="text-bone/80 hover:text-brass transition-colors text-[12px] uppercase tracking-[0.28em] inline-flex items-center gap-2">
              Read the Full Menu <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 gap-x-12 gap-y-4">
            {MENU.slice(0, 2).map((chapter) => (
              <div key={chapter.key} data-testid={`featured-chapter-${chapter.key}`} className="border-t border-bone/10 pt-8">
                <div className="flex items-baseline justify-between">
                  <h3 className="font-display text-bone text-3xl">{chapter.title}</h3>
                  <p className="label-eyebrow text-bone/40">Chapter</p>
                </div>
                <p className="text-bone/55 mt-2 italic font-display text-lg">{chapter.blurb}</p>
                <ul className="mt-6 divide-y divide-bone/10">
                  {chapter.items.slice(0, 3).map((item) => (
                    <li key={item.name} className="py-4 flex items-baseline gap-4">
                      <span className="text-bone">{item.name}</span>
                      <span className="flex-1 border-b border-dotted border-bone/20" />
                      <span className="text-brass font-display text-lg">${item.price}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING — botanical */}
      <section data-testid="home-closing" className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={BOTANICAL_IMAGE} alt="" className="h-full w-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian via-obsidian/70 to-obsidian" />
        </div>
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 py-28 md:py-40 text-center">
          <p className="label-eyebrow text-lavender">An Invitation</p>
          <h2 className="font-display text-bone text-4xl md:text-7xl mt-6 max-w-3xl mx-auto leading-[1.05] text-balance">
            Step inside the House.
          </h2>
          <p className="mt-6 text-bone/70 max-w-xl mx-auto">
            We open the door 111 times. Then we close it. Stewards are numbered for life.
          </p>
          <Link
            to="/membership"
            data-testid="home-closing-cta"
            className="mt-10 inline-flex items-center gap-2 px-8 py-4 bg-lavender text-obsidian text-[12px] uppercase tracking-[0.3em] hover:bg-bone transition-colors"
          >
            Reserve a Founding Number <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
